import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotificationProvider } from 'hooks';
import { 
    getAllFolders, 
    getUserFolderPermissions, 
    setUserFolderPermissions,
    prepareFolderPermissionsPayload 
} from 'api/users/folderPermissionsApi';

/**
 * @function useFolderPermissions
 * 
 * Custom hook for managing folder permissions with button-based actions.
 * - Button-based bulk actions
 * - Individual permission changes with immediate feedback
 * - Undo functionality for last 5 actions (history stack)
 * - Improved dependency management to reduce unnecessary re-renders
 * - Search filtering for large folder lists
 * - Mode-aware API loading (create vs edit)
 * 
 * @param {string} mode - "create" or "edit" mode
 * @param {number|null} userId - User ID required for edit mode, null for create mode
 * @returns {Object} Hook interface with state and handlers
 */
const useFolderPermissions = (mode, userId) => {
    const [folders, setFolders] = useState([]);
    const [previousStates, setPreviousStates] = useState([]); // History stack for 5 undo actions
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const { notifySuccess, notifyError } = useNotificationProvider();

    /**
     * Save current state for undo functionality (maintains last 5 actions)
     * @param {Array} currentFolders - Current folders state to save
     */
    const saveStateForUndo = useCallback((currentFolders) => {
        setPreviousStates(prev => {
            const newHistory = [...prev, [...currentFolders]];
            return newHistory.length > 5 ? newHistory.slice(-5) : newHistory;
        });
    }, []);

    /**
     * Load folders based on mode
     * Create mode: All folders with "None" permissions
     * Edit mode: User's current folder permissions
     */
    const loadFolders = useCallback(async () => {
        try {
            setLoading(true);
            let response;
            
            if (mode === "create") {
                response = await getAllFolders();
            } else if (mode === "edit" && userId) {
                response = await getUserFolderPermissions(userId);
            } else {
                throw new Error("Invalid mode or missing user ID for edit mode");
            }
            
            const loadedFolders = response.Data || [];
            setFolders(loadedFolders);
            
            // Initialize history stack
            setPreviousStates([]);
            
        } catch (error) {
            notifyError(`Failed to load folder permissions: ${error.message}`);
            setFolders([]);
            setPreviousStates([]);
        } finally {
            setLoading(false);
        }
    }, [mode, userId, notifyError]);

    /**
     * Handle individual permission changes
     * @param {number} directoryId - Directory ID to update
     * @param {string} permission - New permission value ("None", "View", "Admin")
     */
    const handlePermissionChange = useCallback((directoryId, permission) => {
        try {
            setFolders(currentFolders => {
                // Save current state for undo BEFORE making changes
                saveStateForUndo(currentFolders);
                
                // Return updated folders
                return currentFolders.map(folder =>
                    folder.DirectoryId === directoryId
                        ? { ...folder, Permission: permission }
                        : folder
                );
            });
        } catch (error) {
            console.error('Error in individual permission change:', error);
        }
    }, [saveStateForUndo]);

    /**
     * Handle All Admin button
     * Sets all folders to Admin permission
     */
    const handleAllAdmin = useCallback(() => {
        try {
            setFolders(currentFolders => {
                // Save current state for undo
                saveStateForUndo(currentFolders);
                
                // Set all folders to Admin
                return currentFolders.map(folder => ({ 
                    ...folder, 
                    Permission: "Admin" 
                }));
            });
        } catch (error) {
            console.error('Error in creating all admin permissions:', error);
        }
    }, [saveStateForUndo]);

    /**
     * Handle All View button
     * Sets all folders to View permission
     */
    const handleAllView = useCallback(() => {
        try {
            setFolders(currentFolders => {
                // Save current state for undo
                saveStateForUndo(currentFolders);
                
                // Set all folders to View
                return currentFolders.map(folder => ({ 
                    ...folder, 
                    Permission: "View" 
                }));
            });
        } catch (error) {
            console.error('Error in creating all view permissions:', error);
        }
    }, [saveStateForUndo]);

    /**
     * Handle Clear All button
     * Resets all permissions to "None"
     */
    const handleClearAll = useCallback(() => {
        try {
            setFolders(currentFolders => {
                // Save current state for undo
                saveStateForUndo(currentFolders);
                
                // Clear all permissions
                return currentFolders.map(folder => ({ 
                    ...folder, 
                    Permission: "None" 
                }));
            });
        } catch (error) {
            console.error('Error in clearing all permissions:', error);
        }
    }, [saveStateForUndo]);

    /**
     * Handle Undo button
     * Restores previous state from history stack
     */
    const handleUndo = useCallback(() => {
        try {
            if (previousStates.length > 0) {
                // Get the most recent state from history
                const lastState = previousStates[previousStates.length - 1];
                setFolders([...lastState]);
                
                // Remove the last state from history
                setPreviousStates(prev => prev.slice(0, -1));
            }
        } catch (error) {
            console.error('Error in handling undo operation:', error);
        }
    }, [previousStates]);

    /**
     * Save permissions to backend (edit mode only)
     * @returns {Promise}
     */
    const savePermissions = useCallback(async () => {
        if (mode !== "edit" || !userId) {
            throw new Error("Save operation only available in edit mode with valid user ID");
        }

        try {
            setLoading(true);
            
            // Get current folders state for save operation
            const currentFolders = folders;
            
            // Prepare payload with only non-"None" permissions
            const payload = prepareFolderPermissionsPayload(currentFolders, userId, false);
            const response = await setUserFolderPermissions(payload);
            
            const stats = response.Data;
            const assignedCount = stats.PermissionsAssigned || 0;
            const removedCount = stats.PermissionsSkipped || 0;
            
            notifySuccess(
                `Folder permissions updated successfully. ` +
                `${assignedCount} permissions assigned. ${removedCount ? `${removedCount} skipped.` : ''}`
            );
            
            return response;
        } catch (error) {
            notifyError(`Failed to save folder permissions: ${error.message}`);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [mode, userId, folders, notifySuccess, notifyError]);

    /**
     * Check if undo is available
     */
    const canUndo = useMemo(() => previousStates.length > 0, [previousStates.length]);

    /**
     * Load folders when component mounts
     */
    useEffect(() => {
        if (mode && (mode === "create" || (mode === "edit" && userId))) {
            loadFolders();
        }
    }, [mode, userId, loadFolders]);

    /**
     * Filter folders based on search term
     */
    const filteredFolders = useMemo(() => 
        folders.filter(folder =>
            folder.DirectoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (folder.ParentPath && folder.ParentPath.toLowerCase().includes(searchTerm.toLowerCase()))
        ),
        [folders, searchTerm]
    );

    return {
        // State
        folders: filteredFolders,
        loading,
        searchTerm,
        canUndo,
        
        // Setters
        setSearchTerm,
        
        // Handlers
        handlePermissionChange,
        handleAllAdmin,
        handleAllView,
        handleClearAll,
        handleUndo,
        savePermissions,
        loadFolders
    };
};

export default useFolderPermissions;
